import L from "leaflet";
// El plugin se importa ACÁ, que es donde se usa, y no se confía en que lo haya
// importado otro módulo: el orden de evaluación de los imports decide si
// `L.GeometryUtil` existe o no cuando este archivo corre.
import "leaflet-geometryutil";

/** Indicador de SNAP: punto blanco que aparece bajo el cursor cuando éste
 *  entra en el radio de un borde o un vértice de las piezas existentes, para
 *  avisar que el click NO va a caer donde está el cursor sino exactamente
 *  ahí. Es **decorativo**: `interactive: false` y `pointer-events: none` en su
 *  CSS, porque si capturara el click se comería tanto el del mapa como el del
 *  marcador de cierre que puede quedar debajo — el mismo bug que tuvo el radar
 *  del paso 3. */
const SNAP_POINT_ICON = new L.DivIcon({
  iconSize: [16, 16],
  className: "lote-split-snap-point",
});

/** Radio de snap en píxeles — mismo valor que el control original. */
const SNAP_RADIUS_PX = 15;

/** Modo de pegado.
 *
 *  - `"bordes"`: pega a cualquier punto del borde, y el vértice sólo gana si
 *    está dentro del radio. Es lo que necesita la línea de corte, que tiene que
 *    poder entrar al lote por el medio de una arista.
 *  - `"vertices"`: pega ÚNICAMENTE a los vértices. Es lo que necesita el dibujo
 *    de un lote nuevo, por dos motivos: el borde compartido queda formado por
 *    los MISMOS puntos que el del vecino (un punto sobre la arista está cerca,
 *    pero no es el mismo), y es la condición para que el servidor pueda
 *    autocerrar el trazo — busca los extremos en el anillo del padre, y un
 *    punto intermedio de una arista no está ahí. */
export type ModoSnap = "bordes" | "vertices";

/** `closestLayerSnap` NO es parte de Leaflet ni de `leaflet-draw` — viene del
 *  plugin aparte `leaflet-geometryutil` (equivalente npm de
 *  `Leaflet.GeometryUtil`/`Leaflet.Snap`, que el control original carga como
 *  script suelto). Sin tipos oficiales (`declare module` en
 *  `src/types/declarations.d.ts`), de ahí el cast.
 *
 *  **Se lee en cada llamada, NO se captura en una constante de módulo.** El
 *  plugin instala `L.GeometryUtil` como efecto de su import, así que una
 *  constante de módulo se queda con lo que hubiera en ese instante — y si este
 *  archivo se evalúa antes que el plugin, se queda con `undefined` para
 *  siempre. Eso rompió el snap de los dos motores del paso 4 sin dar ninguna
 *  señal: la llamada tiraba, el `catch` la tragaba y `buscar` devolvía `null`,
 *  o sea "no hay nada cerca". */
function geometryUtil(): {
  closestLayerSnap: (
    map: L.Map,
    layers: L.Layer[],
    latlng: L.LatLng,
    tolerance: number,
    vertices: boolean,
  ) => { latlng: L.LatLng } | null;
} | null {
  return (L as unknown as { GeometryUtil?: never }).GeometryUtil ?? null;
}

/** Pegado de puntos a los bordes de geometrías ya existentes, con su
 *  indicador visual.
 *
 *  **Por qué importa acá y no es un adorno.** Los lotes de una finca comparten
 *  bordes; si un vértice nuevo cae "casi" sobre el borde del vecino en vez de
 *  exactamente encima, queda una franja de solape o una rendija. Es poco ancha,
 *  pero a lo largo de un borde de cientos de metros suma área suficiente para
 *  que el guardado final la rechace (ver `LoteSplitState._sellarBordes`, que
 *  documenta un caso medido). El snap hace que el punto sea literalmente el
 *  mismo.
 *
 *  Lo comparten el trazo de corte del paso 4 (`LoteSplitLineEngine`) y el
 *  dibujo de polígonos (`AoiDrawEngine`, que lo usa solo cuando quien lo monta
 *  le pasa bordes — el paso 3 no le pasa ninguno y por lo tanto no snapea). */
export class MapSnap {
  /** `icono` cambia el indicador: el dibujo del paso 4 usa un punto igual a los
   *  vértices del trazo, para que todo lo que se dibuja se vea igual. */
  constructor(
    private readonly _map: L.Map,
    private readonly _modo: ModoSnap = "bordes",
    private readonly _icono: L.DivIcon = SNAP_POINT_ICON,
  ) {}

  /** Contra qué bordes pegar. Cada entrada es un anillo. Pasar una lista
   *  vacía apaga el snap.
   *
   *  Hay que volver a llamarlo cuando las geometrías cambian: la guía se arma
   *  con una copia de los anillos, no los sigue en vivo. */
  setBordes(bordes: readonly L.LatLng[][]): void {
    this._quitarGuia();
    if (bordes.length === 0) return;

    // Los vértices se guardan SIEMPRE: son los datos con los que trabaja el modo
    // `"vertices"`, que no necesita nada más.
    this._vertices = bordes.flat();

    // **El modo `"vertices"` NO pone nada en el mapa**, y es el punto.
    //
    // Acá se creaba igual un `L.FeatureGroup` con una polilínea por anillo y se
    // agregaba al mapa. Eran invisibles (`opacity: 0`), pero capas de Leaflet de
    // verdad: una por cada anillo de cada lote y de cada polígono padre, que
    // Leaflet reproyecta en cada movimiento y en cada zoom. O sea, la geometría
    // de todos los lotes renderizándose en Leaflet mientras se dibuja —
    // exactamente lo que el paso 4 dejó de hacer cuando los lotes pasaron a
    // GeoServer.
    //
    // Y no servían para nada en este modo: `_verticeCercano` recorre `_vertices`
    // y no toca el grupo. La capa existe sólo para `closestLayerSnap`, que es lo
    // que mide el modo `"bordes"` —el trazo de corte, que tiene que poder entrar
    // por el medio de una arista— porque ahí el candidato no es un vértice y hay
    // que calcularlo contra la geometría.
    if (this._modo === "vertices") return;

    // Una polilínea invisible por anillo, que es contra lo que mide
    // `closestLayerSnap`.
    this._capa = new L.FeatureGroup();
    bordes.forEach((anillo) => {
      this._capa?.addLayer(L.polyline(anillo, { opacity: 0, weight: 0 }));
    });
    this._capa.addTo(this._map);
  }

  /** El punto pegado al borde o vértice más cercano, o `null` si el cursor
   *  está fuera del radio. `vertices: true` hace que, si hay un vértice dentro
   *  del radio, gane sobre un punto cualquiera de la arista — que es lo que se
   *  quiere: apoyarse en los vértices existentes siempre que se pueda, porque
   *  un vértice compartido es exacto y un punto sobre la arista solo está
   *  cerca. */
  buscar(latlng: L.LatLng): L.LatLng | null {
    if (this._modo === "vertices") return this._verticeCercano(latlng);
    if (!this._capa) return null;
    const util = geometryUtil();
    if (!util) return null;
    try {
      const snap = util.closestLayerSnap(
        this._map,
        this._capa.getLayers(),
        latlng,
        SNAP_RADIUS_PX,
        true,
      );
      return snap?.latlng ?? null;
    } catch {
      return null;
    }
  }

  /** El vértice más cercano dentro del radio, o `null`.
   *
   *  Se mide en PÍXELES y no en grados para que el radio se sienta igual a
   *  cualquier zoom, que es lo mismo que hace `closestLayerSnap`. Y se recorre
   *  a mano en vez de pedirle al plugin `vertices: true`: ese modo sigue
   *  devolviendo un punto sobre la arista cuando no hay ningún vértice cerca,
   *  y acá lo que no está sobre un vértice no sirve. */
  private _verticeCercano(latlng: L.LatLng): L.LatLng | null {
    const cursor = this._map.latLngToContainerPoint(latlng);
    let mejor: L.LatLng | null = null;
    let menor = SNAP_RADIUS_PX;

    for (const vertice of this._vertices) {
      const distancia = this._map
        .latLngToContainerPoint(vertice)
        .distanceTo(cursor);
      if (distancia > menor) continue;
      menor = distancia;
      mejor = vertice;
    }
    return mejor;
  }

  /** El punto pegado, o el original si no hay nada dentro del radio. */
  aplicar(latlng: L.LatLng): L.LatLng {
    return this.buscar(latlng) ?? latlng;
  }

  /** Muestra (o mueve) el punto blanco en la posición pegada, o lo esconde si
   *  no hay snap. Se reusa el mismo marcador con `setLatLng` en vez de crear y
   *  destruir uno por cada `mousemove`. */
  mostrar(latlng: L.LatLng | null): void {
    if (!latlng) {
      this.ocultar();
      return;
    }
    if (this._marcador) {
      this._marcador.setLatLng(latlng);
      return;
    }
    this._marcador = L.marker(latlng, {
      icon: this._icono,
      interactive: false,
      keyboard: false,
      // Por debajo de los vértices del trazo: si el cursor cae sobre el
      // último, el que se tiene que ver (y clickear) es el de cierre.
      zIndexOffset: -500,
    }).addTo(this._map);
  }

  ocultar(): void {
    if (!this._marcador) return;
    this._map.removeLayer(this._marcador);
    this._marcador = null;
  }

  dispose(): void {
    this._quitarGuia();
  }

  private _capa: L.FeatureGroup | null = null;
  private _marcador: L.Marker | null = null;
  /** Todos los vértices de los anillos, aplanados. Los usa el modo
   *  `"vertices"`; el modo `"bordes"` no los mira. */
  private _vertices: L.LatLng[] = [];

  private _quitarGuia(): void {
    this.ocultar();
    this._vertices = [];
    if (!this._capa) return;
    this._map.removeLayer(this._capa);
    this._capa = null;
  }
}
